import * as React from "react";
import { RootState } from "../../redux";
import { ConnectedProps, connect } from "react-redux";
import { IPageSettings } from "../../interfaces/IPageSettings";
import { IFile } from "../../interfaces/IFile";
import { ISharePointFolder } from "../../interfaces/ISharePointFolder";
import {
  DocubirdCache,
  FileSortOptionKeys,
  initialFileOptionPanelInfoSetting,
  initialFolderOptionPanelInfoSetting,
  initialGraphSearchPager,
  validSaveModes,
} from "../../config/constants";
import { IFileFolderSearchSharepointParams } from "../../interfaces/IFileFolderSearchSharepointParams";
import { getItemCustomSelect, searchDriveForFilesAndFoldersSharepoint } from "../../services/GraphService";
import { DriveSearchResultMapper } from "../helpers/DriveSearchResultMapper";
import { UrlSearchBuilderFactory } from "../helpers/sharepointSearch/factory/UrlSearchBuilderFactory";
import {
  ActionButton,
  IIconProps,
  IStackStyles,
  Spinner,
  SpinnerSize,
  Stack
} from "@fluentui/react";
import { LanguageContext } from "../../../../utilities/LocalizationModule";
import { IFilePanelInfo } from "../../interfaces/IFilePanelInfo";
import { IFolderPanelInfo } from "../../interfaces/IFolderPanelInfo";
import { FolderTypeEnum } from "../enums/FolderTypeEnum";
import { ComponentTypes } from "../../config/config";
import { setSearch } from "../../redux/modules/search";
import {
  resetWorkingDirectories,
  addWorkingDirectories,
  addWorkingDirectory,
} from "../../redux/modules/workingDirectory";
import {
  setComposeFiles,
  setComposeVisibility,
  setFileExpander
} from "../../redux/modules/file";
import { expanderConstants } from "../../redux/models/constants/expanders";
import ExpanderWithSubtitle from "../directoryComponents/ExpanderWithSubtitle";
import FileOptionPanel from "../panels/FileOptionPanel";
import FolderOptionPanel from "../panels/FolderOptionPanel";
import { SearchedSharepointFoldersDirectoryBuilder } from "../helpers/searchedSharepointFoldersDirectoryBuilder";
import { IGraphSearchPager } from "../../interfaces/IGraphSearchPager";
import savingLocationStructureModule from "../../../../utilities/SavingLocationStructureModule";
import { isMobile } from "../../styles/mobileStyles";
import { logger } from "../../../../utilities/logger";
import { WebPanel } from "../_v2/_shared/WebPanel/WebPanel";
import MultipleFilesSeleted from "../panels/MultipleFilesSeleted";
import authModule from "../../../../utilities/AuthModule";
import { Fileview } from "../_v2/components/Explorer/Fileview/Fileview";
import { PIIResult } from "../_v2/redux/api/local/api-local-interfaces";
import Loader from "../Loader";
import { FileSortOptions } from "./fileSortOptions";
import { ISortOption } from "../../interfaces/ISortOption";
import { FileSortOptionHelper } from "../helpers/fileSortOptionHelper";
import { SharePointFolderItem } from "../_v2/components/Explorer/FolderView/SharePointFolderItem";
import { ModeEnum } from "../enums/modeEnum";
import { FilterTypeEnum } from "../enums/FilterTypeEnum";
import { SearchValue } from "../../redux/models/reduxModels";
import { IDirectoryBuilder } from "../../interfaces/IDirectoryBuilder";
import { SearchedOneDriveDirectoryBuilder } from "../helpers/searchedOneDriveDirectoryBuilder";
import { CacheHelper } from "../../../../utilities/CacheHelper";
import { filterStrictDriveItemSearchResults } from "../../../../utilities/SearchUtil";
import { ChevronDoubleDown20Regular } from "../_v2/components/SvgIcons/ChevronDoubleDown20Regular";

const searchIconSrc = require("../../../../assets/empty_search.svg")

const EmptySearchStackStyles: Partial<IStackStyles> = {
  root: { position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)", userSelect: "none" }
};

const mapDispatchToProps = {
  resetWorkingDirectories,
  addWorkingDirectory,
  addWorkingDirectories,
  setSearch,
  setFileExpander,
  setComposeFiles,
  setComposeVisibility,
};

const mapStateToProps = (state: RootState) => {
  return {
    currentDirectory:
      state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1],
    workingDirectories: state.workingDirectories.workingDirectories,
    folderPermissions: state.currentSharepointFolder,
    searchValue: state.search.searchValue.value,
    mode: state.mode.mode,
    canView: state.currentSharepointFolder.canView,
    sites: state.sharePointInfo.sites,
    isLoading: state.loading.isLoading,
    oneDriveWebUrl: state.user.oneDriveWebUrl,
    teams: state.teams.teams,
    hasFileSelected: state.files.files.some((file) => file.isSelected),
    user: state.user,
    searchValueFull: state.search.searchValue
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface FileAndFoldersSearchProps extends PropsFromRedux {
  parentViewHasItems: boolean;
  isSearchingCallback?: (isSearching: boolean) => void;
  searchResultsAvailableCallback?: (resultsAvailable: boolean) => void;
  isLoadingCallback?: (isLoading: boolean) => void;
}

const loadMoreResultsIcon: IIconProps = { iconName: "Table" };

const FileAndFoldersSharepointSearch: React.FunctionComponent<FileAndFoldersSearchProps> = (props) => {
  const languageStrings = React.useContext(LanguageContext);
  const [folders, setFolders] = React.useState<ISharePointFolder[]>([]);
  const [files, setFiles] = React.useState<IFile[]>([]);
  const [selectedFiles, setSelectedFiles] = React.useState<IFile[]>([]);
  const [hasSearched, setHasSearched] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [fileOptionPanelInfo, setFileOptionPanelInfo] = React.useState<IFilePanelInfo>(
    initialFileOptionPanelInfoSetting
  );
  const [folderOptionPanelInfo, setFolderOptionPanelInfo] = React.useState<IFolderPanelInfo>(
    initialFolderOptionPanelInfoSetting
  );

  const currentDirectoryPrivate = props.currentDirectory?.type === undefined
    ? { type: ComponentTypes.GeneralSearch, isTeamsFolder: false }
    : props.currentDirectory;

  const [urlSearchFactory] = React.useState(
    UrlSearchBuilderFactory.createUrlSearchBuilder(
      currentDirectoryPrivate.type,
      [],
      props.oneDriveWebUrl
    )
  );
  const searchFoldersPager = React.useRef<IGraphSearchPager>(initialGraphSearchPager);
  const searchFilesPager = React.useRef<IGraphSearchPager>(initialGraphSearchPager);

  const [isLoadingMoreFolderResults, setIsLoadingMoreFolderResults] = React.useState<boolean>(false);
  const [isLoadingMoreFileResults, setIsLoadingMoreFileResults] = React.useState<boolean>(false);

  const [isMultipleFilesSelectedPanelOpen, setIsMultipleFilesSelectedPanelOpen] = React.useState(false);

  const [sortOption, setSortOption] = React.useState<ISortOption>();

  React.useEffect(() => {
    if (props.mode === ModeEnum.mailCompose) {
      props.setComposeFiles({ selectedFiles, isGraphQuery: true });
    }
  }, [props.mode, selectedFiles]);

  React.useEffect(() => {
    if (props.mode === ModeEnum.mailCompose) {
      props.setComposeVisibility(files?.length > 0);
    }
  }, [props.mode, files]);

  React.useEffect(() => {
    const setupAsyncWrapper = async () => {
      if (!props.searchValue && !hasSearched) return;

      setSelectedFiles([]);

      if (!props.searchValue && hasSearched) {
        resetSearch();
        return;
      }

      resetSearchPagers();

      if (props.isSearchingCallback) props.isSearchingCallback(true);
      setIsLoading(true);
      setHasSearched(true);
      resetSearchPagers();

      try {
        const [folderResultCount, fileResultCount] = await Promise.all([getSearchedFolders(), getSearchedFiles()]);
        if (props.searchResultsAvailableCallback) {
          props.searchResultsAvailableCallback(folderResultCount > 0 || fileResultCount > 0);
        }
      } catch (error) {
        logger(error, "fileAndFoldersSharepoint.tsx (useEffect)");
      }

      setIsLoading(false);
      if (props.isSearchingCallback) props.isSearchingCallback(false);

      props.setFileExpander(false);
    };
    setupAsyncWrapper();
  }, [props.searchValueFull]);

  const resetSearch = async () => {
    resetSearchPagers();
    setFiles([]);
    setFolders([]);
  };

  const resetSearchPagers = () => {
    searchFoldersPager.current = initialGraphSearchPager;
    searchFilesPager.current = initialGraphSearchPager;
  };

  const getSearchedFolders = async (isLoadMore?: boolean, autoSearchDepth: number = 0) => {
    if (props?.searchValueFull.filterType !== "" && props?.searchValueFull.filterType !== FilterTypeEnum.Folder) {
      setFolders([]);
      searchFoldersPager.current = initialGraphSearchPager;
      return 0;
    }

    if (isLoadMore) {
      setIsLoadingMoreFolderResults(true);
    }

    const hasValidSearchValue: boolean = !!props.searchValueFull?.value?.trim();
    const isStrictSearch: boolean = hasValidSearchValue && CacheHelper.get(DocubirdCache.isStrictSearch);

    const folderSearchParams = buildSearchParams(urlSearchFactory.buildFolderQuery, searchFoldersPager.current.from);
    let result: { moreResultsAvailable: boolean; driveItems: ISharePointFolder[] } =
      await searchDriveForFilesAndFoldersSharepoint(folderSearchParams, new DriveSearchResultMapper(), isStrictSearch && autoSearchDepth > 0 ? 250 : 30);

    searchFoldersPager.current = {
      moreResultsAvailable: result.moreResultsAvailable,
      from: result.moreResultsAvailable ? searchFoldersPager.current.from + result.driveItems.length : 0,
    }

    let folderResults: ISharePointFolder[] = result.driveItems.map((item) => ({ ...item, isSearched: true }));

    if (currentDirectoryPrivate.type === ComponentTypes.SharePoint) {
      folderResults = folderResults.filter(
        (item) => !props.oneDriveWebUrl || !item.webUrl?.includes(props.oneDriveWebUrl)
      );
    }

    if (isStrictSearch) {
      folderResults = filterStrictDriveItemSearchResults(folderResults, props.searchValueFull.value);
    }

    setFolders((prevFolders) => isLoadMore ? [...prevFolders, ...folderResults] : folderResults);

    const currentResultCount: number = result?.driveItems?.length ?? 0;

    if (result.moreResultsAvailable && autoSearchDepth < 5 && folderResults.length < 30) {
      getSearchedFolders(true, ++autoSearchDepth);
      return currentResultCount;
    }

    if (isLoadMore) {
      setIsLoadingMoreFolderResults(false);
    }

    let totalResultCount: number = isLoadMore ? folders?.length ?? 0 : 0;
    return totalResultCount + currentResultCount;
  };

  const getSearchedFiles = async (isLoadMore?: boolean, sortOption?: ISortOption, pageSize: number = 30, autoSearchDepth: number = 0) => {
    if (props?.searchValueFull.filterType == FilterTypeEnum.Folder) {
      setFiles([]);
      searchFilesPager.current = initialGraphSearchPager;
      return 0;
    }

    if (isLoadMore) {
      setIsLoadingMoreFileResults(true);
    }

    const hasValidSearchValue: boolean = !!props.searchValueFull?.value?.trim();
    const isStrictSearch: boolean = hasValidSearchValue && CacheHelper.get(DocubirdCache.isStrictSearch);

    const fileSearchParams = buildSearchParams(urlSearchFactory.buildFileQuery, searchFilesPager.current.from);
    fileSearchParams.sortProperties = FileSortOptionHelper.getGraphSearchSortOptions(sortOption);
    const result = await searchDriveForFilesAndFoldersSharepoint(fileSearchParams, new DriveSearchResultMapper(), isStrictSearch && autoSearchDepth > 0 ? 250 : pageSize);

    searchFilesPager.current = {
      moreResultsAvailable: result.moreResultsAvailable,
      from: result.moreResultsAvailable ? searchFilesPager.current.from + result.driveItems.length : 0,
    }

    let fileResults: IFile[] = result.driveItems.map((item: any) => ({ ...item, hasGraphItemId: true }));

    if (isStrictSearch) {
      fileResults = filterStrictDriveItemSearchResults(fileResults, props.searchValueFull.value);
    }

    setFiles(prevFiles => {
      const newFiles = isLoadMore ? [...prevFiles, ...fileResults] : fileResults;
      return sortOption ? FileSortOptionHelper.sort(newFiles, sortOption) : newFiles;
    });

    const currentResultCount: number = result?.driveItems?.length ?? 0;

    if (result.moreResultsAvailable && autoSearchDepth < 5 && fileResults.length < pageSize) {
      getSearchedFiles(true, sortOption, pageSize, ++autoSearchDepth);
      return currentResultCount;
    }

    if (isLoadMore) {
      setIsLoadingMoreFileResults(false);
    }

    const totalResultCount: number = isLoadMore ? files?.length ?? 0 : 0;
    return totalResultCount + currentResultCount;
  };

  const buildSearchParams = (query: (currentDirectory: IPageSettings, searchValue: SearchValue) => string, from: number) => {
    const folderqueryString = query(currentDirectoryPrivate, props.searchValueFull);
    return buildSearchParametersWithWebUrl(folderqueryString, from);
  };

  const buildSearchParametersWithWebUrl = (queryString: string, from: number): IFileFolderSearchSharepointParams => {
    return {
      queryString: queryString,
      from: from,
      extraDriveInfo: {
        siteUrl: currentDirectoryPrivate?.webUrl,
      },
    };
  };

  const addFolderDirectory = async (item: ISharePointFolder) => {
    setIsLoading(true);
    if (props.isLoadingCallback) props.isLoadingCallback(true);
    props.setSearch("");

    try {
      let directoryBuilder: IDirectoryBuilder;

      if (!item.webUrl?.includes(props.oneDriveWebUrl)) {
        directoryBuilder = new SearchedSharepointFoldersDirectoryBuilder(
          props.sites,
          props.teams,
          currentDirectoryPrivate?.type,
          props?.currentDirectory?.isTeamsFolder || props?.currentDirectory?.type === ComponentTypes.Teams
        );
      }
      else if (props.user.oneDriveWebUrl) {
        const itemDetails = await getItemCustomSelect(await authModule.getGraphAccessToken(), item.driveId, item.id, "sharepointIds");
        if (itemDetails) {
          const listId: string = itemDetails.sharepointIds?.listId;
          directoryBuilder = listId && new SearchedOneDriveDirectoryBuilder(props.user.oneDriveWebUrl, listId);
        }
      }

      // const directoryBuilder = new SearchedSharepointFoldersDirectoryBuilder(
      //   props.sites,
      //   props.teams,
      //   currentDirectoryPrivate?.type,
      //   props?.currentDirectory?.isTeamsFolder || props?.currentDirectory?.type === ComponentTypes.Teams
      // );

      if (directoryBuilder) {
        const directories = await directoryBuilder.getFolderDirectories(item);
        props.resetWorkingDirectories(directories);
      }

    } catch (error) {
      logger(error, "fileAndFoldersSharepoint.tsx (addFolderDirectory)");
    }

    if (props.isLoadingCallback) props.isLoadingCallback(false);
    setIsLoading(false);
  };

  const onSaveClicked = React.useCallback(async (item: ISharePointFolder) => {
    setIsLoading(true);
    if (props.isLoadingCallback) props.isLoadingCallback(true);

    try {
      const directoryBuilder = new SearchedSharepointFoldersDirectoryBuilder(
        props.sites,
        props.teams,
        currentDirectoryPrivate?.type,
        props?.currentDirectory?.isTeamsFolder || props?.currentDirectory?.type === ComponentTypes.Teams
      );

      const directories = await directoryBuilder.getFolderDirectories(item);
      savingLocationStructureModule.setSavingLocationStructure(directories);

      props.addWorkingDirectory({
        ...directories[directories.length - 1],
        type: ComponentTypes.Saving,
        displayName: languageStrings.Properties,
        isSaveLocationStructureSet: true,
      });
    } catch (error) {
      logger(error, "fileAndFoldersSharepoint.tsx (onSaveClicked)");
    }

    props.setSearch("");
    if (props.isLoadingCallback) props.isLoadingCallback(false);
    setIsLoading(false);
  }, [props]);

  const openFileOptions = (fileInfo: IFile) => {
    setFileOptionPanelInfo({
      ...fileInfo,
      isOpen: true,
      itemId: fileInfo?.hasGraphItemId ? fileInfo.id : null,
      folderId: !currentDirectoryPrivate?.parentId ? null : currentDirectoryPrivate.id,
      filePath:
        fileInfo?.hasGraphItemId && fileInfo?.path
          ? `[${languageStrings.Files}] ${props.workingDirectories
            .slice(0, -1)
            .map((x) => x.displayName)
            .join("/")}${fileInfo.path}`
          : `[${languageStrings.Files}] ${props.workingDirectories.map((x) => x.displayName).join("/")}/${fileInfo.name
          }`,
    });
  };

  const dismissFileOptionPanel = React.useCallback(() => {
    setFileOptionPanelInfo(initialFileOptionPanelInfoSetting);
  }, []);

  const openFolderOptions = React.useCallback((folderInfo: ISharePointFolder) => {
    setFolderOptionPanelInfo({
      isOpen: true,
      folder: folderInfo,
      folderType: FolderTypeEnum.sharepoint,
      isTeamsFolder: currentDirectoryPrivate.isTeamsFolder,
    });
  }, [currentDirectoryPrivate.isTeamsFolder]);

  const dismissFolderOptionPanel = React.useCallback(() => {
    setFolderOptionPanelInfo(initialFolderOptionPanelInfoSetting);
  }, []);

  const selectFile = (item: IFile) => {
    const newFiles = [...files];
    const selectedFile = newFiles.find((c) => c.id === item.id);
    if (selectedFile) {
      selectedFile.selected = selectedFile.selected ? !selectedFile.selected : true;
    }
    setFiles(newFiles);
    setSelectedFiles([...newFiles.filter((x) => x.selected === true)]);
  };

  const isValidDropZone = React.useMemo((): boolean => {
    return validSaveModes.some(item => item === props.mode);
  }, [props.mode]);

  const folderLoadMoreButton = () => {
    return isLoadingMoreFolderResults ? (
      <div style={{ paddingTop: "10px" }}>
        <Spinner size={SpinnerSize.medium} />
      </div>
    ) : (
      searchFoldersPager.current.moreResultsAvailable && (
        <ActionButton
          onRenderIcon={() => <ChevronDoubleDown20Regular />}
          allowDisabledFocus
          onClick={async () => await getSearchedFolders(true)}
        >
          {languageStrings.LoadMore}
        </ActionButton>
      )
    );
  };

  const fileLoadMoreButton = () => {
    return isLoadingMoreFileResults ? (
      <div style={{ paddingTop: "10px" }}>
        <Spinner size={SpinnerSize.medium} />
      </div>
    ) : (
      searchFilesPager.current.moreResultsAvailable && (
        <ActionButton
          onRenderIcon={() => <ChevronDoubleDown20Regular />}
          allowDisabledFocus
          onClick={async (e) => { e.preventDefault(); await getSearchedFiles(true, sortOption) }}
        >
          {languageStrings.LoadMore}
        </ActionButton>
      )
    );
  };

  const updateFilePii = (fileOptionPanelInfo: IFilePanelInfo, piiResult: PIIResult) => {
    setFiles(files.map(file => {
      return {
        ...file,
        pii: file.id === fileOptionPanelInfo.id ? piiResult : file.pii
      }
    }));
  }

  const fileUpdated = (fileOptionPanelInfo: IFilePanelInfo, newFilename: string) => {
    if (props?.searchValueFull.filterType == FilterTypeEnum.Folder) {
      setFiles([]);
      return;
    }
    setFiles(files.map(file => {
      return {
        ...file,
        name: file.id === fileOptionPanelInfo.id ? newFilename : file.name
      }
    }));
  }

  const updateMultipleFilesPii = (selectedFiles: IFile[]) => {
    setFiles(files.map(file => {
      const selectedFile = selectedFiles.find(x => x.id === file.id);
      return {
        ...file,
        pii: selectedFile ? selectedFile.pii : file.pii
      }
    }));
  }

  const fileDeleted = (fileOptionPanelInfo: IFilePanelInfo) => {
    setFiles(files.filter(x => x.id !== fileOptionPanelInfo.id));
    setSelectedFiles([]);
  }

  const filesDeleted = () => {
    setFiles(files.filter((el) => !selectedFiles.includes(el)));
    setSelectedFiles([]);
    setIsMultipleFilesSelectedPanelOpen(false);
  }

  const onSortApplied = (sortOption: ISortOption) => {
    setSortOption(sortOption);

    if (sortOption.sortField === FileSortOptionKeys.SortFields.Type) {
      setFiles(prev => FileSortOptionHelper.sort([...prev], sortOption));
    }
    else {
      searchFilesPager.current = initialGraphSearchPager;
      getSearchedFiles(false, sortOption);
    }
  }

  return isLoading ? (
    <Loader label={languageStrings.SearchingContent} />
  ) : (
    <>
      {folders?.length > 0 && (
        <>
          <ExpanderWithSubtitle
            items={folders.map((item, index) => (
              <SharePointFolderItem
                key={index}
                item={item}
                isMobile={isMobile}
                hasFileSelected={props.hasFileSelected}
                isValidDropZone={isValidDropZone}
                addFolderDirectory={addFolderDirectory}
                onSaveClicked={onSaveClicked}
                openFolderOptions={openFolderOptions}
              />
            ))}
            title={languageStrings.Folders}
            expanderNameToToggle={expanderConstants.sharepoint.folders}
            extra={folderLoadMoreButton()}
            hasShowMoreButton={files?.length > 0}
          />
          {folderOptionPanelInfo?.isOpen && <FolderOptionPanel info={folderOptionPanelInfo} dismissPanel={dismissFolderOptionPanel} />}
        </>
      )}
      {files?.length > 0 && (
        <>
          <ExpanderWithSubtitle
            items={files.map((item, index) => (
              <div key={index}>
                <Fileview
                  item={item}
                  isMobile={isMobile}
                  tenantEnablePII={props.user.tenantEnablePII}
                  selectFile={selectFile}
                  openFileOptions={openFileOptions}
                />
              </div>
            ))}
            itemsSelected={selectedFiles.length}
            title={languageStrings.Files}
            expanderNameToToggle={expanderConstants.sharepoint.files}
            extra={fileLoadMoreButton()}
            hasShowMoreButton={folders?.length > 0}
            selectedItemsMoreClicked={() => setIsMultipleFilesSelectedPanelOpen(true)}
            sortOptions={<FileSortOptions addRelevanceOption={true} onSortApplied={onSortApplied} />}
          />
          {fileOptionPanelInfo?.isOpen && <FileOptionPanel info={fileOptionPanelInfo} dismissPanel={dismissFileOptionPanel} updateFilePii={updateFilePii} fileUpdated={fileUpdated} fileDeleted={fileDeleted} />}
          {isMultipleFilesSelectedPanelOpen && (
            <WebPanel
              isOpen={isMultipleFilesSelectedPanelOpen}
              closePanel={() => setIsMultipleFilesSelectedPanelOpen(false)}
              headerText={`${selectedFiles.length} ${selectedFiles.length === 1
                ? languageStrings.FileSelected.toLowerCase()
                : languageStrings.FilesSelected.toLowerCase()
                }`}
              styles={{ main: { overflow: 'unset' } }}
            >
              <MultipleFilesSeleted
                files={selectedFiles}
                isGraphQuery={true}
                currentDirectory={currentDirectoryPrivate}
                workingDirectories={props.workingDirectories}
                updateMultipleFilesPii={updateMultipleFilesPii}
                filesDeleted={filesDeleted}
                setIsMultipleFilesSelectedPanelOpen={setIsMultipleFilesSelectedPanelOpen}
              />
            </WebPanel>
          )}
        </>
      )}
      {!props.parentViewHasItems && !(folders?.length > 0) && !(files?.length > 0) && (
        <Stack verticalAlign="center" horizontalAlign="center" styles={EmptySearchStackStyles}>
          <Stack.Item align="center">
            <img src={searchIconSrc} style={{ height: 165 }} />
          </Stack.Item>
          <Stack.Item align="center">
            <span className="xb-empty-location-message centerText">{languageStrings.GenericNoResultsFoundText}</span>
          </Stack.Item>
        </Stack>
      )}
    </>
  );
};

export default connector(FileAndFoldersSharepointSearch);